/** Utilidades de formato de fechas/horas en español. */

const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });

/** Tiempo relativo compacto ("hace 5 min", "ayer"). */
export function relativeTime(input?: string | null): string {
  if (!input) return '';
  const date = new Date(input);
  const diff = date.getTime() - Date.now();
  const abs = Math.abs(diff);
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (abs < min) return 'ahora';
  if (abs < hour) return rtf.format(Math.round(diff / min), 'minute');
  if (abs < day) return rtf.format(Math.round(diff / hour), 'hour');
  if (abs < 7 * day) return rtf.format(Math.round(diff / day), 'day');
  return date.toLocaleDateString('es', { day: '2-digit', month: 'short' });
}

/**
 * Tiempo para la lista de conversaciones (Bug #4). Más expresivo que
 * `relativeTime`: distingue hoy / ayer / esta semana / más viejo.
 *  - < 1 min            → "ahora"
 *  - < 60 min           → "hace N min"
 *  - hoy (≥ 1 h)        → "HH:MM"
 *  - ayer               → "ayer HH:MM"
 *  - últimos 7 días     → "Mar 14:30"
 *  - más viejo          → "14 mar"
 */
export function conversationTime(input?: string | null): string {
  if (!input) return '';
  const date = new Date(input);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const min = 60_000;

  if (diff < min && diff > -min) return 'ahora';
  if (diff >= min && diff < 60 * min) {
    return rtf.format(-Math.round(diff / min), 'minute');
  }

  const hhmm = date.toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
  const startOfDay = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((startOfDay(now) - startOfDay(date)) / (24 * 60 * min));

  if (dayDiff <= 0) return hhmm;
  if (dayDiff === 1) return `ayer ${hhmm}`;
  if (dayDiff < 7) {
    const dow = date.toLocaleDateString('es', { weekday: 'short' }).replace('.', '');
    const cap = dow.charAt(0).toUpperCase() + dow.slice(1);
    return `${cap} ${hhmm}`;
  }
  return date.toLocaleDateString('es', { day: '2-digit', month: 'short' });
}

/** Códigos de área de República Dominicana (NANP). */
const DR_AREA_CODES = new Set(['809', '829', '849']);

/**
 * Formatea un número de teléfono para mostrarlo en la UI. Los contactos guardan
 * el teléfono como dígitos crudos (p.ej. "18095550102", derivado del JID de
 * WhatsApp). Para números de RD devuelve el formato bonito "+1 849 631 4646";
 * para cualquier otro país devuelve el valor tal cual (no asumimos formato).
 */
export function formatPhoneNumber(input?: string | null): string {
  if (!input) return '';
  const digits = input.replace(/\D/g, '');
  if (!digits) return input;

  // RD con código de país: 1 + AAA + 7 dígitos (11 en total).
  if (digits.length === 11 && digits.startsWith('1') && DR_AREA_CODES.has(digits.slice(1, 4))) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  }
  // RD sin código de país: AAA + 7 dígitos (10 en total).
  if (digits.length === 10 && DR_AREA_CODES.has(digits.slice(0, 3))) {
    return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  // Otros países: lo dejamos tal cual.
  return input;
}

/** Hora corta (HH:MM). */
export function shortTime(input?: string | null): string {
  if (!input) return '';
  return new Date(input).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' });
}

/** Fecha y hora legibles. */
export function dateTime(input?: string | null): string {
  if (!input) return '';
  return new Date(input).toLocaleString('es', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
